import PropTypes from 'prop-types';
import React from 'react';
import differenceInMilliseconds from 'date-fns/difference_in_milliseconds';
import TimedView from '../../../../common/timer/TimedView';
import {
    FLUENCY_MIN_SEGMENT_READING_SECS, FLUENCY_READING_REMINDER_DELAY,
    FLUENCY_DOWN_BUTTON_AUTO_ADVANCE, FLUENCY_QUESTION_AUTO_ADVANCE,
} from '../../../../constants/time';
import {
    RCT, FLUENCY, FLUENCY_AUTO_ADVANCE_SEGMENTS_COUNT, FLUENCY_PASSAGE_QUESTION_SKIPPED,
} from '../../../../constants/measure';
import { READING_REMINDER_TEXT } from '../../../../constants/text';
import AudioContainer from '../../../../common/audio/audioContainer';
import GoButton from '../../../../common/components/GoButton';
import PassageTitle from '../../../../common/components/PassageTitle';
import SegmentedPassage from '../../../../common/components/SegmentedPassage';
import PopupQuestion from '../../../../common/components/PopupQuestion';
import TriangleButton from '../../../../common/components/TriangleButton';
import PassageLayout from '../../../../common/layouts/passageLayout';
import ScreenErrorContainer from '../../../../common/error/ScreenErrorContainer';
import MessagePopup from '../../../../common/components/MessagePopup';
import { Dim } from '../../../../constants/components';

// TODO: extract to a separate file, so it can be imported by test hooks to mock specific timer
const REMINDER_TIMER = 'reminderTimer';
const MAX_SEGMENT_READING_TIMER = 'segmentReadingTimer';
const MAX_QUESTION_ANSWER_TIMER = 'questionTimer';

export default class RCTFluencyPassageView extends TimedView {
    constructor(props) {
        super(props);
        this.onRetry = this.onRetry.bind(this);
        this.onDownButtonClick = this.onDownButtonClick.bind(this);
        this.hideReadingReminder = this.hideReadingReminder.bind(this);
        this.reminderOnDisplayTimestamp = undefined;
        this.onResponseSelected = this.onResponseSelected.bind(this);
        this.questionAutoAdvance = this.questionAutoAdvance.bind(this);
        this.onSegmentReadingAutoAdvance = this.onSegmentReadingAutoAdvance.bind(this);
    }

    componentDidMount() {
        this.props.initializePassage();
        if (!this.props.isDataFetched) {
            this.props.fetchContent();
        } else if (!this.props.areResultsCreated) {
            this.props.createResults();
        } else if (this.props.areResultsCreated && this.props.activePassageIndex === 0) {
            this.props.recreateFluencyComprehensionResults();
            // BUG: time tracking for first segment reading is started twice
            // TODO: separate ready script reading and time tracking
            // the method below has two responsibilities:
            // 1. play ready script in case it wasn't played yet
            // 2. start time tracking for first segment reading not depending on ready script state
            // these events may or may not be сoupled for different scinarios: regular flow,
            // resumed flow for the first and other passages, page refresh.
            this.startScreenSequence();
        } else {
            this.startScreenSequence();
        }
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.isDataFetched && this.props.isDataFetched) {
            if (!prevProps.areResultsCreated) {
                this.props.createResults();
            } else {
                this.startScreenSequence();
            }
        }

        if (!prevProps.areResultsCreated && this.props.areResultsCreated) {
            this.startScreenSequence();
        }

        if (this.shouldResetReminderTimer(prevProps)) {
            this.createStopwatch(REMINDER_TIMER);
        }

        if (prevProps.isReminderPopupActive && !this.props.isReminderPopupActive) {
            this.resumeTimers();
        }

        if (this.allowDownButtonTimeTracking(prevProps)) {
            this.startDownButtonTimeTracking();
        }
        if (this.allowQuestionTimeTracking(prevProps)) {
            this.startQuestionTimeTracking();
        }
    }

    componentWillUnmount() {
        this.resetTimers();
        this.props.cleanup();
    }

    onRetry() {
        if (!this.props.areResultsCreated) {
            this.props.createResults();
        } else if (this.props.isNextButtonActive && !this.props.isGreyedOut) {
            this.props.onNext(this.props.activePassageIndex);
        } else if (this.props.isQuestionVisible) {
            this.props.onResponseSelected(this.props.activePassageIndex);
        } else if (!this.props.isDownButtonEnabled && this.props.isScreenBlocked) {
            this.props.onSegmentReadingComplete(this.props.activePassageIndex);
        } else if (this.props.areResultsCreated && this.props.activePassageIndex === 0) {
            this.props.recreateFluencyComprehensionResults();
        }
    }

    onDownButtonClick() {
        const reminderTimer = this.getTimer(REMINDER_TIMER);
        if (reminderTimer) {
            if (reminderTimer.getTime()
                    < FLUENCY_MIN_SEGMENT_READING_SECS[this.props.studentGrade]) {
                this.props.showReadingReminder();
                this.pauseTimers();
                this.reminderOnDisplayTimestamp = new Date();
                return;
            }
        }

        this.resetTimers();
        this.props.onSegmentReadingComplete(this.props.activePassageIndex);
    }

    shouldResetReminderTimer(prevProps) {
        return prevProps.activeSegmentIndex < FLUENCY_AUTO_ADVANCE_SEGMENTS_COUNT
            && !prevProps.isDownButtonEnabled && this.props.isDownButtonEnabled;
    }

    hideReadingReminder() {
        let reminderOnDisplayTime = 0;
        if (this.reminderOnDisplayTimestamp) {
            reminderOnDisplayTime = differenceInMilliseconds(new Date(), this.reminderOnDisplayTimestamp);
        }

        this.props.hideReadingReminder(reminderOnDisplayTime);

        this.reminderOnDisplayTimestamp = undefined;
    }

    onResponseSelected(index) {
        this.resetTimers();
        this.props.onResponseSelected(this.props.activePassageIndex, index);
    }

    onSegmentReadingAutoAdvance() {
        this.props.onSegmentReadingComplete(this.props.activePassageIndex);
    }

    questionAutoAdvance() {
        this.props.onResponseSelected(this.props.activePassageIndex,
            FLUENCY_PASSAGE_QUESTION_SKIPPED.index);
    }

    allowDownButtonTimeTracking(prevProps) {
        return !prevProps.isDownButtonEnabled && this.props.isDownButtonEnabled
            && !this.props.isGreyedOut && this.props.isReadyScriptPlayed
            && this.props.activeSegmentIndex < FLUENCY_AUTO_ADVANCE_SEGMENTS_COUNT;
    }

    allowQuestionTimeTracking(prevProps) {
        return !prevProps.isQuestionVisible && this.props.isQuestionVisible
            && this.props.isReadyScriptPlayed && this.props.activeSegmentIndex !== undefined;
    }

    startDownButtonTimeTracking() {
        this.scheduleEvent(MAX_SEGMENT_READING_TIMER, this.onSegmentReadingAutoAdvance,
            FLUENCY_DOWN_BUTTON_AUTO_ADVANCE[this.props.studentGrade]);
    }

    startQuestionTimeTracking() {
        this.scheduleEvent(MAX_QUESTION_ANSWER_TIMER, this.questionAutoAdvance,
            FLUENCY_QUESTION_AUTO_ADVANCE.FOR_ALL_GRADES);
    }

    startScreenSequence() {
        this.props.readyScript();
        this.createStopwatch(REMINDER_TIMER);
    }

    render() {
        return (
            <PassageLayout
                class={`${RCT}-${FLUENCY}`}
                isScreenBlocked={this.props.isScreenBlocked}
                isGreyedOut={this.props.isGreyedOut}>
                <ScreenErrorContainer onRetry={this.onRetry} />
                { this.props.isReminderPopupActive
                        && (
                            <MessagePopup
                                delayedCallback={this.hideReadingReminder}
                                delay={FLUENCY_READING_REMINDER_DELAY}
                                message={READING_REMINDER_TEXT}
                                backgroundDim={Dim.CENTER} />
                        ) }
                <AudioContainer />
                <PassageTitle
                    subTitle={`Text ${this.props.activePassageIndex + 1} of
                            ${this.props.passageLength}`}
                    title={this.props.passageTitle} />
                <div className="page-wrapper">
                    <div className="fluency-wrapper">
                        {(this.props.isDataFetched
                                    && this.props.activeSegmentIndex !== undefined)
                                && (
                                    <SegmentedPassage
                                        segments={this.props.segments}
                                        isActiveSegmentHidden={!this.props.isSegmentVisible}
                                        activeSegmentIndex={this.props.activeSegmentIndex}
                                        onShow={this.props.onSegmentShow} />
                                )}
                        <div className="fluency-questions">
                            {this.props.question
                                        && (
                                            <PopupQuestion
                                                question={this.props.question.stimulus.text}
                                                possibleResponses={
                                                    this.props.question.possibleResponses
                                                }
                                                onResponseSelected={this.onResponseSelected}
                                                selectedResponse={this.props.selectedResponse}
                                                isHidden={!this.props.isQuestionVisible}
                                                onHide={() => {
                                                    this.props.onPopupQuestionHide(
                                                        this.props.activePassageIndex,
                                                    );
                                                }} />
                                        )}
                        </div>
                        <div
                            className={`fluency-overlay${this.props.isNextButtonActive
                                ? ' hide-horizontal-line' : ''}`}>
                            {!this.props.isNextButtonActive
                                    && (
                                        <TriangleButton
                                            style={this.props.isSegmentVisible}
                                            isEnabled={this.props.isDownButtonEnabled}
                                            onClick={this.onDownButtonClick} />
                                    ) }
                        </div>
                    </div>
                </div>
                {this.props.isNextButtonActive
                        && (
                            <GoButton
                                isEnabled={!this.props.isScreenBlocked}
                                onClick={() => { this.props.onNext(this.props.activePassageIndex); }}
                            />
                        )}
            </PassageLayout>
        );
    }
}

RCTFluencyPassageView.propTypes = {
    isDataFetched: PropTypes.bool,
    areResultsCreated: PropTypes.bool,
    isScreenBlocked: PropTypes.bool,
    isGreyedOut: PropTypes.bool,
    isNextButtonActive: PropTypes.bool,
    isDownButtonEnabled: PropTypes.bool,
    isQuestionVisible: PropTypes.bool,
    isSegmentVisible: PropTypes.bool,
    segments: PropTypes.array,
    question: PropTypes.object,
    cleanup: PropTypes.func,
    fetchContent: PropTypes.func,
    createResults: PropTypes.func,
    onResponseSelected: PropTypes.func,
    initializePassage: PropTypes.func,
    onSegmentReadingComplete: PropTypes.func,
    onPopupQuestionHide: PropTypes.func,
    onSegmentShow: PropTypes.func,
    readyScript: PropTypes.func,
    onNext: PropTypes.func,
    activeSegmentIndex: PropTypes.number,
    selectedResponse: PropTypes.number,
    /* eslint-disable react/no-unused-prop-types */
    activePassageIndex: PropTypes.number,
    isReadyScriptPlayed: PropTypes.bool,
    /* eslint-enable react/no-unused-prop-types */
    passageTitle: PropTypes.string,
    passageLength: PropTypes.number,
    showReadingReminder: PropTypes.func,
    hideReadingReminder: PropTypes.func,
    isReminderPopupActive: PropTypes.bool,
    studentGrade: PropTypes.string,
    discontinueStudent: PropTypes.func,
    recreateFluencyComprehensionResults: PropTypes.func,
};


RCTFluencyPassageView.defaultProps = {
    isDataFetched: false,
    areResultsCreated: false,
    isScreenBlocked: false,
    isGreyedOut: false,
    isNextButtonActive: false,
    isDownButtonEnabled: false,
    isQuestionVisible: false,
    isSegmentVisible: false,
    segments: [],
    question: undefined,
    cleanup: () => {},
    fetchContent: () => {},
    createResults: () => {},
    onResponseSelected: () => {},
    initializePassage: () => {},
    onSegmentReadingComplete: () => {},
    onPopupQuestionHide: () => {},
    onSegmentShow: () => {},
    activePassageIndex: undefined,
    readyScript: () => {},
    onNext: () => {},
    activeSegmentIndex: undefined,
    selectedResponse: undefined,
    passageTitle: '',
    passageLength: 0,
    showReadingReminder: () => {},
    hideReadingReminder: () => {},
    isReminderPopupActive: false,
    discontinueStudent: () => {},
    isReadyScriptPlayed: false,
    recreateFluencyComprehensionResults: () => {},
};

RCTFluencyPassageView.displayName = 'RCTFluencyPassageView';
